import { Button, FormControl, FormHelperText, FormLabel, Input, Link, Stack } from '@mui/joy';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { arrayMoveImmutable } from 'array-move';
import React, { useEffect, useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import ArrowIcon from '@/assets/icons/arrow.svg?react';
import CrossIcon from '@/assets/icons/cross.svg?react';
import EditIcon from '@/assets/icons/edit.svg?react';
import OrientationIcon from '@/assets/icons/orientation-arrow.svg?react';
import { FloatingInput } from '@/components/form-elements/FloatingInput';
import { RadioWithDescriptionGroup } from '@/components/form-elements/RadioWithDescriptionGroup';
import { SignCodeIcon } from '@/components/signs/SignCodeIcon';
import { EditModalDialog } from '@/components/signs/modal/EditModalDialog';
import { I18N_DE } from '@/i18n/de';
import { Sign, SignSupportType, UpdateSignSupportDto } from '@/modules/api';
import { backendApiSignSupportsUpdateMutation } from '@/modules/api/@tanstack/react-query.gen';
import { useSignSupportQuery } from '@/queries/useSignSupportQuery';
import { theme } from '@/setup/theme';
import { useCreateSignStore } from '@/stores/useCreateSignStore';
import { useMapStore } from '@/stores/useMapStore';
import { useSignsTableStore } from '@/stores/useSignsTableStore';
import { LocationInputMode } from '@/types/LocationInputMode';
import { SignsTableMode } from '@/types/SignsTableState';

export const Route = createFileRoute(
  '/_authenticated/_management/verkehrszeichen/$adminLevelId/$signSupportId/bearbeiten',
)({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: SignSupportEditRoute,
});

type FormData = Pick<UpdateSignSupportDto, 'description' | 'type'>;

function SignSupportEditRoute() {
  const { adminLevelId, signSupportId } = Route.useParams();
  const navigate = useNavigate();
  const formId = useId();

  const { data: support } = useSignSupportQuery();

  const geometry = useCreateSignStore((state) => state.geometry);
  const { setLocationInputMode, resetStore, setSupportId, reloadSupportLayer } = useCreateSignStore(
    (state) => state.actions,
  );

  const { setMode } = useSignsTableStore((state) => state.actions);

  const { setSnackbar } = useMapStore((state) => state.actions);

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormData>();

  const [groupsOrder, setGroupsOrder] = useState<{ id: string; bearing?: number; signs?: Sign[] }[]>();

  useEffect(() => {
    reset({ ...support });
    setGroupsOrder(
      support?.groups?.map(({ id, signs, bearing }) => ({
        id,
        signs,
        bearing,
      })),
    );
  }, [support, reset]);

  const { mutate, error, isPending } = useMutation({
    ...backendApiSignSupportsUpdateMutation(),
    onSuccess: async () => {
      reloadSupportLayer();
      resetStore();
      navigate({
        to: '/verkehrszeichen/$adminLevelId/$signSupportId',
        params: { adminLevelId, signSupportId },
      });
    },
  });

  const compareOrder = () =>
    JSON.stringify(groupsOrder?.map(({ id }) => ({ id }))) ===
    JSON.stringify(support?.groups?.map(({ id }) => ({ id })));

  return (
    <EditModalDialog
      title="Aufstellvorrichtung bearbeiten"
      onClose={() => {
        resetStore();
        navigate({
          to: '/verkehrszeichen/$adminLevelId/$signSupportId',
          params: { adminLevelId, signSupportId },
        });
      }}
    >
      <Stack
        component="form"
        id={formId}
        onSubmit={handleSubmit(({ type, description }) => {
          if (isPending) return;

          mutate({
            path: { signSupportId: support?.id! },
            body: {
              type,
              description,
              ...(geometry && { geometry }),
              ...(!compareOrder() && {
                groupsOrder: groupsOrder?.map(({ id }) => id),
              }),
            },
          });
        })}
        gap={2}
      >
        <Stack direction="row" gap={2}>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Button
            variant="outlined"
            startDecorator={<EditIcon />}
            onClick={() => {
              if (support) {
                resetStore();
                setSupportId(support.id);
                setMode(SignsTableMode.MAP);
                setLocationInputMode(LocationInputMode.DRAG);
                setSnackbar({
                  color: 'neutral',
                  text: 'Position bearbeiten',
                  startDecorator: <EditIcon stroke={theme.palette.text.secondary} />,
                  onClose: () => {
                    resetStore();
                    setSnackbar(undefined);
                    navigate({
                      to: '/verkehrszeichen/$adminLevelId/$signSupportId/bearbeiten',
                      params: { adminLevelId, signSupportId },
                    });
                  },
                  onApply: () => {
                    setSnackbar(undefined);
                    navigate({
                      to: '/verkehrszeichen/$adminLevelId/$signSupportId/bearbeiten',
                      params: { adminLevelId, signSupportId },
                    });
                  },
                });
                navigate({
                  to: '/verkehrszeichen/$adminLevelId/$signSupportId',
                  params: { adminLevelId, signSupportId },
                });
              }
            }}
          >
            Position bearbeiten
          </Button>

          {geometry && (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <Link component="button" startDecorator={<CrossIcon />} onClick={() => resetStore()}>
              zurücksetzen
            </Link>
          )}
        </Stack>

        <FormLabel>Reihenfolge</FormLabel>

        {groupsOrder?.map(({ id, bearing, signs }, index, array) => (
          <Stack key={id} direction="row" spacing={1}>
            <Stack direction="column">
              {index !== 0 && (
                <Button
                  variant="plain"
                  size="sm"
                  onClick={() => {
                    setGroupsOrder(arrayMoveImmutable(array, index, index - 1));
                  }}
                >
                  <ArrowIcon style={{ transform: 'rotate(-180deg)' }} />
                </Button>
              )}
              {index !== array.length - 1 && (
                <Button
                  variant="plain"
                  size="sm"
                  onClick={() => {
                    setGroupsOrder(arrayMoveImmutable(array, index, index + 1));
                  }}
                >
                  <ArrowIcon />
                </Button>
              )}
            </Stack>

            <OrientationIcon
              style={{
                transform: `rotate(${bearing}deg)`,
                width: 30,
                height: 30,
                margin: 'auto 20px',
              }}
            />

            {signs?.map((sign) => (
              <React.Fragment key={sign.id}>
                <SignCodeIcon code={sign.code} style={{ width: 40, height: 40 }} deleted={sign.status === 'DELETED'} />
              </React.Fragment>
            ))}
          </Stack>
        ))}

        <FormControl error={!!errors.type}>
          <FormLabel>Typ</FormLabel>
          <Controller
            control={control}
            name="type"
            render={({ field }) => (
              <RadioWithDescriptionGroup
                {...field}
                orientation="horizontal"
                options={Object.values(SignSupportType)}
                optionLabel={({ option }) => I18N_DE.SignSupportType[option]}
              />
            )}
          />
        </FormControl>

        <FormControl error={!!errors.description}>
          <FormLabel>Beschreibung</FormLabel>
          <Input
            type="text"
            variant="outlined"
            slots={{ input: FloatingInput }}
            slotProps={{
              input: {
                ...register('description'),
                label: 'optional',
              },
            }}
          />
        </FormControl>

        {error && <FormHelperText>{error.message}</FormHelperText>}

        <Stack direction="row" gap={2}>
          <Button type="submit" form={formId} loading={isPending} disabled={isPending}>
            Speichern
          </Button>
        </Stack>
      </Stack>
    </EditModalDialog>
  );
}
